import { getSafeExternalUrl } from '@/libs/utils/safeExternalUrl';
import {
  PinakesStatus,
  type TPinakesAlsoOn,
  type TPinakesCard,
  type TPinakesResourceDetail,
} from '@/services/pinakes/pinakes.types';
import { ResourceFamily } from './ResourceCard.types';

/**
 * schema.org types whose cards carry extra details, nearest ancestor wins (see `getResourceFamily`).
 * A family is a group of types, never one type: every type below `Event` or `Place` in a card's
 * `type_path` gets that family's details, whatever Pinakes adds later.
 */
const FAMILY_ANCESTORS: Readonly<Record<string, ResourceFamily>> = {
  Event: ResourceFamily.EVENT,
  Place: ResourceFamily.PLACE,
};

/** A schema.org media type ends in `Object` (`VideoObject`, `ImageObject`); the label drops it. */
const MEDIA_OBJECT_SUFFIX = /(?<=.)Object$/;

/** Splits CamelCase into words and keeps acronyms whole: `FAQPage` → `FAQ Page`, `TVSeries` → `TV Series`. */
const CAMEL_CASE_BOUNDARY = /(?<=[a-z0-9])(?=[A-Z])|(?<=[A-Z])(?=[A-Z][a-z])/g;

/**
 * The card's type in plain words, from its schema.org `type` (else the deepest `type_path` entry):
 * `MusicRecording` → `Music recording`, `VideoObject` → `Video`, `FAQPage` → `FAQ page`.
 * `null` when the card has no type.
 */
export function getResourceTypeLabel(card: Pick<TPinakesCard, 'type' | 'type_path'>): string | null {
  const type = card.type || card.type_path.at(-1);
  if (!type) return null;

  const words = type.replace(MEDIA_OBJECT_SUFFIX, '').split(CAMEL_CASE_BOUNDARY);
  return words
    .map((word, index) => {
      // An acronym (`FAQ`, `TV`) keeps its case; every other word after the first is lower case.
      if (index === 0 || /^[A-Z0-9]+$/.test(word)) return word;
      return word.toLowerCase();
    })
    .join(' ');
}

/**
 * Which details a card shows, from the nearest ancestor in its `type_path` that has a family
 * (`Restaurant` → `LocalBusiness` → `Place`). Types with no such ancestor are plain web cards.
 */
export function getResourceFamily(card: Pick<TPinakesCard, 'type' | 'type_path'>): ResourceFamily {
  const path = card.type && card.type_path.at(-1) !== card.type ? [...card.type_path, card.type] : card.type_path;
  for (let index = path.length - 1; index >= 0; index--) {
    const family = FAMILY_ANCESTORS[path[index]];
    if (family) return family;
  }
  return ResourceFamily.WEB;
}

/** The card's `status` as one the card knows how to show: an unknown value reads as `error`. */
export function getResourceStatus(status: string): PinakesStatus {
  const known = Object.values(PinakesStatus).find((value) => value === status);
  return known ?? PinakesStatus.ERROR;
}

/**
 * `value` when it is an `http:` or `https:` URL, else `null`. Only these may become an `href` or an
 * image `src`: Pinakes URIs can be `isbn:`, `doi:`, `pubky://`, and a stored value could be anything.
 */
export function getHttpUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  const url = getSafeExternalUrl(value);
  if (!url) return null;
  const { protocol } = new URL(url);
  return protocol === 'http:' || protocol === 'https:' ? url : null;
}

/** A link without its scheme and `www.`, for a card that has no name: `https://www.x.com/a` → `x.com/a`. */
export function getDisplayLink(value: string): string {
  return value.replace(/^[a-z][a-z0-9+.-]*:(\/\/)?/i, '').replace(/^www\./i, '');
}

/**
 * schema.org types whose `image` is a 16:9 frame (a video still, a film's or series' backdrop):
 * the card shows it as a 16:9 thumbnail, where a square crop would cut it (and any text in it).
 * Every other card (music, books, places, articles) keeps the square.
 */
const WIDE_IMAGE_TYPES: ReadonlySet<string> = new Set([
  'VideoObject',
  'Clip',
  'Movie',
  'MovieSeries',
  'TVSeries',
  'TVSeason',
  'TVEpisode',
]);

/** Whether the card's image is a 16:9 frame, from its `type` or any `type_path` entry. */
export function hasWideImage(card: Pick<TPinakesCard, 'type' | 'type_path'>): boolean {
  return (card.type ? [card.type, ...card.type_path] : card.type_path).some((type) => WIDE_IMAGE_TYPES.has(type));
}

/** Types whose schema.org name is not the plain word people use for them, for the tag bar's label. */
const TAGS_LABEL_NOUNS: Readonly<Record<string, string>> = {
  MusicRecording: 'song',
  MusicAlbum: 'album',
  MusicGroup: 'artist',
  Movie: 'film',
  TVSeries: 'TV series',
};

/**
 * The tag bar's label, naming what is tagged in plain words from the card's type: "Tags on this
 * song", "Tags on this video", "Tags on this FAQ page". Without a type (no card, or an untyped
 * one) it is "Tags on the external resource".
 */
export function getResourceTagsLabel(card: Pick<TPinakesCard, 'type' | 'type_path'> | null | undefined): string {
  const noun = card ? TAGS_LABEL_NOUNS[card.type || card.type_path.at(-1) || ''] : undefined;
  if (noun) return `Tags on this ${noun}`;
  const typeLabel = card ? getResourceTypeLabel(card) : null;
  if (!typeLabel) return 'Tags on the external resource';
  const [first, ...rest] = typeLabel.split(' ');
  // An acronym (`FAQ`, `TV`) keeps its case mid-sentence; any other first word reads in lower case.
  const lead = /^[A-Z0-9]{2,}$/.test(first) ? first : first.charAt(0).toLowerCase() + first.slice(1);
  return `Tags on this ${[lead, ...rest].join(' ')}`;
}

/** Controls a click on the card must reach, not the card's click-through. */
const CARD_CONTROL_SELECTOR =
  'a, button, input, textarea, select, label, [role="button"], [role="menuitem"], [data-resource-controls]';

/**
 * Whether a click on a card lands on the card itself rather than on one of its controls: not a
 * link, button, field or menu item, not inside the card's action row or tag bar, and not in a
 * portal (a menu or dialog the card opened, whose events bubble through the React tree).
 */
export function isCardSurfaceClick(card: Element, target: EventTarget | null): boolean {
  if (!(target instanceof Element) || !card.contains(target)) return false;
  return !target.closest(CARD_CONTROL_SELECTOR);
}

/** The host of an `http(s)` link without `www.`, else `null`. */
export function getLinkHost(value: string | null | undefined): string | null {
  const url = getHttpUrl(value);
  return url ? new URL(url).hostname.replace(/^www\./i, '') : null;
}

/**
 * `https://<host>/favicon.ico` for an `http(s)` link, else `null` (non-web URIs have no site to ask).
 * Always `https:`: the app is served over `https:`, where an `http:` image is mixed content.
 */
export function getFaviconUrl(value: string | null | undefined): string | null {
  const url = getHttpUrl(value);
  return url ? `https://${new URL(url).hostname}/favicon.ico` : null;
}

/**
 * Where a card leads and whose site it names, shared by the card and its Open in menu:
 *
 * - `linkUrl`: the post's link first (what the author wrote), then the card's own link, then the
 *   address Pinakes gives for a Pubky app's object (`open_in`); never a non-web URI.
 * - `sourceUri`: what the source's favicon is read from: the card's own link when that is web, not
 *   an alias the post may use (a short link, a mobile host), since the source names the card's site.
 * - `siteName`: the card's `site`, else the host of `sourceUri` without `www.`.
 */
export function getResourceLinks(
  card: Pick<TPinakesCard, 'uri' | 'site' | 'open_in'>,
  href?: string,
): { linkUrl: string | null; sourceUri: string; siteName: string | null } {
  const linkUrl = getHttpUrl(href) ?? getHttpUrl(card.uri) ?? getHttpUrl(card.open_in[0]?.url);
  const sourceUri = getHttpUrl(card.uri) ?? linkUrl ?? card.uri;
  return { linkUrl, sourceUri, siteName: card.site || getLinkHost(sourceUri) };
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** The `event_status` of an event going ahead as planned, as stable sends it. */
const SCHEDULED_EVENT_STATUS = 'scheduled';

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function createDateFormat(locale: string | undefined, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  try {
    return new Intl.DateTimeFormat(locale, options);
  } catch {
    // An unknown IANA zone is a RangeError: show the time in the viewer's own zone instead.
    const inViewerZone = { ...options };
    delete inViewerZone.timeZone;
    delete inViewerZone.timeZoneName;
    return new Intl.DateTimeFormat(locale, inViewerZone);
  }
}

/**
 * When an event happens, in the viewer's locale: in the event's time zone with its name when the
 * card has `tz`, else in the viewer's zone. All-day events show dates only, read in UTC with `end`
 * exclusive, as stable's calendar occurrences encode them (`00:00Z` → next day's `00:00Z`), so a
 * one-day event shows its date alone. `null` without a valid `start`.
 *
 * @param locale - For tests; the viewer's locale when omitted
 */
export function formatResourceDates(
  card: Pick<TPinakesResourceDetail, 'start' | 'end' | 'tz' | 'all_day'>,
  locale?: string,
): string | null {
  const start = parseDate(card.start);
  if (!start) return null;
  const end = parseDate(card.end);

  if (card.all_day) {
    const format = createDateFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' });
    const lastDay = end ? new Date(end.getTime() - DAY_MS) : null;
    if (!lastDay || lastDay.getTime() <= start.getTime()) return format.format(start);
    return format.formatRange(start, lastDay);
  }

  const format = createDateFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(card.tz ? { timeZone: card.tz, timeZoneName: 'short' } : {}),
  });
  if (!end || end.getTime() <= start.getTime()) return format.format(start);
  return format.formatRange(start, end);
}

/**
 * The note for an event that is not going ahead as planned: its `event_status` as written. `null`
 * for `scheduled` or no status.
 */
export function getEventStatusNote(eventStatus: string | null | undefined): string | null {
  const value = eventStatus?.trim();
  if (!value || value.toLowerCase() === SCHEDULED_EVENT_STATUS) return null;
  return `Event status: ${value}`;
}

/** A point as `lat, lon` in degrees, four decimals (about 10 m). */
export function formatResourcePoint(point: { lat: number; lon: number }): string {
  return `${point.lat.toFixed(4)}, ${point.lon.toFixed(4)}`;
}

/** The `kind` of a derived link that is an archived copy; its `at` is the capture. */
const ARCHIVED_KIND = 'archived';

/** A documented `kind` whose plain wording is not its value with a capital: an acronym. */
const KIND_LABELS: Readonly<Record<string, string>> = { pdf: 'PDF' };

/** The name of an Also on entry: its service, else the link's host, else the link as written. */
export function getAlsoOnName(entry: Pick<TPinakesAlsoOn, 'uri' | 'site'>): string {
  return entry.site?.trim() || getLinkHost(entry.uri) || getDisplayLink(entry.uri);
}

/**
 * What an Also on entry says under its name: the identifier of a URI that is not a web link and is
 * named by its service (`doi:10.1038/nature12373` named "DOI"), an archived copy with its capture
 * date, another qualifier (`kind`), and for a derived link (nobody tagged it) the source it was
 * found through. `null` for a tagged web link without a qualifier.
 *
 * The capture date is read in UTC, the zone archives such as the Wayback Machine date captures in.
 *
 * @param locale - For tests; the viewer's locale when omitted
 */
export function getAlsoOnNote(
  entry: Pick<TPinakesAlsoOn, 'uri' | 'site' | 'derived' | 'kind' | 'via' | 'at'>,
  locale?: string,
): string | null {
  const parts: string[] = [];
  const kind = entry.kind?.trim();
  if (kind?.toLowerCase() === ARCHIVED_KIND) {
    const at = parseDate(entry.at);
    parts.push(
      at ? `Archived ${createDateFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(at)}` : 'Archived',
    );
  } else if (kind) {
    parts.push(KIND_LABELS[kind.toLowerCase()] ?? kind);
  }
  if (entry.derived) {
    const via = entry.via?.trim();
    parts.push(via ? `found via ${via}` : 'found automatically');
  }
  const qualifier = parts.length > 0 ? parts.join(' · ') : null;
  const note = qualifier && qualifier.charAt(0).toUpperCase() + qualifier.slice(1);

  // A non-web entry has no link to open: without its identifier, a row named "DOI" says nothing.
  // Kept as written (no capital): an identifier's case can matter.
  const identifier = getHttpUrl(entry.uri) ? null : getDisplayLink(entry.uri);
  if (identifier && identifier !== getAlsoOnName(entry)) {
    return note ? `${identifier} · ${note}` : identifier;
  }
  return note;
}

/**
 * Where a card from a list (Similar) opens: its own link when that is web, else the address a Pubky
 * app gives for it (`open_in`), else its first web link on another service (`also_on`), so an
 * `isbn:` book still opens somewhere. A `missing` card's own link is gone upstream, so it opens
 * elsewhere. `null` when it has no web link at all (a `geo:` point).
 */
export function getResourceLink(card: Pick<TPinakesCard, 'uri' | 'status' | 'open_in' | 'also_on'>): string | null {
  const own = getResourceStatus(card.status) === PinakesStatus.MISSING ? [] : [card.uri];
  const candidates = [...own, ...card.open_in.map((entry) => entry.url), ...card.also_on.map((entry) => entry.uri)];
  for (const candidate of candidates) {
    const url = getHttpUrl(candidate);
    if (url) return url;
  }
  return null;
}

/**
 * The note under a card in a list (Similar): its type in plain words and its site, else the host of
 * the link the row opens. A row that opens another service than the card's own link names that
 * service's host, not the card's site. `null` when it has neither.
 */
export function getResourceListNote(
  card: Pick<TPinakesCard, 'uri' | 'type' | 'type_path' | 'site'>,
  href: string | null,
): string | null {
  const site = !href || href === getHttpUrl(card.uri) ? card.site?.trim() : null;
  const parts = [getResourceTypeLabel(card), site || getLinkHost(href)].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}
